import React, { useState, useEffect, useRef, useCallback } from "react";
import cn from "classnames";
import styles from "./Settings.module.sass";
import Card from "../../components/Card";
import Dropdown from "../../components/Dropdown";
import Table from "./Table";
import Panel from "./Panel";
import Form from "../../components/Form";
import Icon from "../../components/Icon";
import Loader from "../../components/Loader";
import Actions from "../../components/Actions";
import { useNavigate } from "react-router-dom";
import { getFolderCountsByStatus, getFolderList } from "../../services/folder.service";


const Settings = () => {
  const navigate = useNavigate();

  const createOptions = [
    {
      title: "Créer un dossier (autonome)",
      action: () => {
        navigate("/admin/folders/create/auto");
      }
    },
    {
      title: "Créer un dossier (manuel)",
      action: () => {
        navigate("/admin/folders/create/manual");
      }
    }
  ];
  const [counters, setCounters] = useState({
    total: 0,
    pending_school: 0,
    pending_company: 0,
    pending_student: 0,
    pending_opco: 0,
    blocked: 0
  });
  

  const navigation = [
    {
      title: "Tous les dossiers",
      counter: counters.total.toString(),
      colorCounter: "#B5E4CA",
      filter: "all",
      action: () => {}
    },
    {
      title: "En attente de l'école",
      counter: counters.pending_school.toString(),
      colorCounter: "#B5E4CA",
      filter: "pending_school",
      action: () => {}
    },
    {
      title: "En attente de l'entreprise",
      counter: counters.pending_company.toString(),
      colorCounter: "#B5E4CA",
      filter: "pending_company",
      action: () => {}
    },
    {
      title: "En attente de l'étudiant",
      counter: counters.pending_student.toString(),
      colorCounter: "#B5E4CA",
      filter: "pending_student",
      action: () => {}
    },
    {
      title: "En attente de l'OPCO",
      counter: counters.pending_opco.toString(),
      colorCounter: "#B5E4CA",
      filter: "pending_opco",
      action: () => {}
    },
    {
      title: "Dossiers bloqué",
      counter: counters.blocked.toString(),
      colorCounter: "#FFE7E4",
      filter: "blocked",
      action: () => {}
    },
  ];
  
  const options = [];
  navigation.map((x) => options.push(x.title));
  const [activeTab, setActiveTab] = useState(options[0]);
  const [currentFilter, setCurrentFilter] = useState('all');
  const [activeIndex, setActiveIndex] = useState(0);
  const [visible, setVisible] = useState(true); // Visible par défaut
  const [search, setSearch] = useState("");
  const [folders, setFolders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [noResults, setNoResults] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedFolders, setSelectedFolders] = useState([]);

  // Chargement initial des compteurs
  useEffect(() => {
    const fetchCounters = async () => {
      try {
        const response = await getFolderCountsByStatus();
        if (response.success) {
          setCounters(response.data);
        }
      } catch (error) {
        console.error('Erreur lors du chargement des compteurs:', error);
      }
    };

    fetchCounters();
  }, []);

  // Mettre à jour les compteurs lorsque les dossiers changent
  useEffect(() => {
    const fetchCounters = async () => {
      try {
        const response = await getFolderCountsByStatus();
        if (response.success) {
          setCounters(response.data);
        }
      } catch (error) {
        console.error('Erreur lors du chargement des compteurs:', error);
      }
    };

    if (folders.length > 0) {
      fetchCounters();
    }
  }, [folders]);

  // Charger les dossiers lorsque le filtre, la recherche ou la page changent
  useEffect(() => {
    const fetchFolders = async () => {
      setLoading(true);
      setError(null);
      setNoResults(false);
      
      console.log("Chargement des dossiers avec le filtre:", currentFilter);
      
      try {
        const response = await getFolderList(currentPage, 10, search.trim(), currentFilter);
        
        console.log("Réponse reçue:", response);
        
        if (response.success) {
          setFolders(response.data || []);
          setTotalPages(response.totalPages || 1);
          setNoResults(response.data.length === 0 && !!search);
        } else {
          setFolders([]);
          setTotalPages(1);
        }
      } catch (err) {
        console.error("Erreur lors du chargement des dossiers:", err);
        setError("Impossible de charger les dossiers. Veuillez réessayer.");
        setFolders([]);
        setTotalPages(1);
      } finally {
        setLoading(false);
      }
    };

    fetchFolders();
  }, [search, currentPage, currentFilter]); 

  useEffect(() => {
    setCurrentPage(1);
  }, [search, currentFilter]);

  const handleClick = (x, index) => {
    setActiveIndex(index);
    setCurrentFilter(x.filter || 'all');
    setActiveTab(x.title);
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
  };

  const handleFolderSelect = (folder) => {
    console.log('Dossier sélectionné:', folder);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleSearchChange = (value) => {
    setSearch(value);
  };

  const handleFolderSelection = (folderId) => {
    if (selectedFolders.includes(folderId)) {
      setSelectedFolders(selectedFolders.filter(id => id !== folderId));
    } else {
      setSelectedFolders([...selectedFolders, folderId]);
    }
  };

  const handleSelectAll = (isSelected) => {
    if (isSelected) {
      setSelectedFolders(folders.map(folder => folder.id));
    } else {
      setSelectedFolders([]);
    }
  };

  const handleDeleteSelected = async () => {
    // Cette fonction n'est plus nécessaire car la suppression est gérée par le Panel
    console.log('Suppression des dossiers:', selectedFolders);
    setSelectedFolders([]);
  };

  return (
    <>
      <Card
        className={styles.card}
        title="Dossiers"
        classTitle={cn("title-blue", styles.title)}
        classCardHead={cn(styles.head, { [styles.hidden]: visible })}
        head={
          <>
            <Form
              className={styles.form}
              value={search}
              setValue={handleSearchChange}
              onSubmit={handleSubmit}
              placeholder="Rechercher par nom ou email"
              type="text"
              name="search"
              icon="search"
            />

            <Actions
              className={styles.actionsDropdown}
              items={createOptions}
              small
            />
          </>
        }
      >
        <div className={cn(styles.row, { [styles.flex]: visible })}>
          <div className={styles.settings}>
            <div className={styles.menu}>
              {navigation.map((x, index) => (
                <button
                  className={cn(styles.button, {
                    [styles.active]: activeIndex === index,
                  })}
                  key={index}
                  onClick={() => handleClick(x, index)}
                >
                  {x.title}
                  {x.counter && (
                    <span
                      className={styles.counter}
                      style={{ backgroundColor: x.colorCounter }}
                    >
                      {x.counter}
                    </span>
                  )}
                </button>
              ))}
            </div>
            <div className={styles.wrapper}>
              <Dropdown
                className={styles.dropdown}
                classDropdownHead={styles.dropdownHead}
                value={activeTab}
                setValue={setActiveTab}
                options={options}
              />
              <div className={styles.list}>
                <div className={cn(styles.item, { [styles.active]: true })}>
                  <div className={styles.anchor} />
                  
                  {error && <div className={styles.error}>{error}</div>}
                  
                  {loading ? (
                    <div className={styles.loading}>Chargement des dossiers...</div>
                  ) : (
                    <div className={styles.row}>
                      {console.log("🔍 État du rendu:", { 
                        loading, 
                        error, 
                        noResults, 
                        foldersLength: folders.length, 
                        folders: folders.slice(0, 2),
                        search,
                        currentFilter,
                        activeIndex
                      })}
                      {noResults && folders.length === 0 && (
                        <div className={styles.empty}>
                          Aucun dossier trouvé pour votre recherche "{search}".
                        </div>
                      )}
                      {!noResults && folders.length === 0 && !loading && !error && (
                        <div className={styles.empty}>
                          Aucun dossier disponible.
                        </div>
                      )}
                      {folders.length > 0 && (
                        <>
                          <Table
                            className={styles.table}
                            items={folders}
                            onFolderSelect={handleFolderSelect}
                            currentFilter={currentFilter}
                            onSelectionChange={setSelectedFolders}
                          />

                          <div className={styles.foot}>
                            <button 
                              className={styles.arrow} 
                              onClick={() => handlePageChange(Math.max(1, currentPage - 1))} 
                              disabled={currentPage === 1}
                            >
                              <Icon name="arrow-left" size="20" />
                            </button>
                            <span className={styles.pagination}>
                              Page {currentPage} sur {totalPages}
                            </span>
                            <button 
                              className={styles.arrow} 
                              onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))} 
                              disabled={currentPage === totalPages}
                            >
                              <Icon name="arrow-right" size="20" />
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </Card>
      <Panel 
        selectedIds={selectedFolders} 
        onDeleteSuccess={() => {
          setSelectedFolders([]);
          // Recharger les données après suppression
          const fetchFolders = async () => {
            setLoading(true);
            try {
              const response = await getFolderList(currentPage, 10, search.trim(), currentFilter);
              if (response.success) {
                setFolders(response.data || []);
                setTotalPages(response.totalPages || 1);
              }
            } catch (err) {
              console.error("Erreur lors du rechargement des dossiers:", err);
            } finally {
              setLoading(false);
            }
          };
          fetchFolders();
        }}
      />
    </>
  );
};

export default Settings;
